"use client";

import { APIProvider } from "@vis.gl/react-google-maps";
import CustomMap, { Location } from "./custom-map";
import { useState, useEffect } from "react";
import {
  parseKMZFile,
  isPointInPolygon,
  findNearestLocation,
  Polygon,
  parseMapFile,
} from "@/components/locations/find-a-location/map";
import FindCareNearYou from "./find-care-near-you";
import ZoomControls from "./zoom-controls";
import NoLocationNearYourSearch from "./no-location-near-your-search";
import { useWindowSize } from "@/hooks/use-window-size";

export interface FindALocationProps {
  heading: string;
  locations?: Location[];
  showServiceArea?: boolean;
  overrideServiceArea?: boolean;
  serviceAreaKMZ?: string;
}

export const defaultLocations: Location[] = [
  {
    lat: 37.7749,
    lng: -122.4194,
    name: "San Francisco",
    rating: "5.0",
    address: "50 California Street, Suite 1500, San Francisco, CA 94111",
    phone: "+1 (650) 328-1001",
    email: "info@careindeed.com",
    hours: "9:00 AM - 5:30 PM",
    link: "/san-francisco",
  },
  {
    lat: 37.8969,
    lng: -122.0615,
    name: "Walnut Creek",
    rating: "5.0",
    address: "2121 North California Blvd., Suite 290, Walnut Creek, CA 94596",
    phone: "+1 (650) 328-1001",
    email: "info@careindeed.com",
    hours: "9:00 AM - 5:30 PM",
    link: "/walnut-creek",
  },
  {
    lat: 37.563,
    lng: -122.3255,
    name: "San Mateo",
    rating: "5.0",
    address: "102 S El Camino Real, San Mateo, CA 94401",
    phone: "+1 (650) 328-1001",
    email: "info@careindeed.com",
    hours: "9:00 AM - 5:30 PM",
    link: "/san-mateo",
  },
  {
    lat: 37.4419,
    lng: -122.143,
    name: "Palo Alto",
    rating: "5.0",
    address: "228 Hamilton Avenue 3rd Floor Palo Alto CA 94301",
    phone: "+1 (650) 328-1001",
    email: "info@careindeed.com",
    hours: "9:00 AM - 5:30 PM",
    link: "/palo-alto",
  },
  {
    lat: 37.45033579621073,
    lng: -122.18618135767248,
    name: "Menlo Park",
    rating: "5.0",
    address: "890 Santa Cruz Ave, Suite B Menlo Park, CA 94025",
    phone: "+1 (650) 328-1001",
    email: "info@careindeed.com",
    hours: "9:00 AM - 5:30 PM",
    link: "/menlo-park",
  },
  {
    lat: 37.32212658000374,
    lng: -121.94665593068997,
    name: "San Jose",
    rating: "5.0",
    address: "358 S Redwood Ave, San Jose, CA 95128",
    phone: "+1 (650) 328-1001",
    email: "info@careindeed.com",
    hours: "9:00 AM - 5:30 PM",
    link: "/san-jose",
  },
  {
    lat: 37.29469586514765,
    lng: -121.94221652890285,
    name: "Campbell",
    rating: "5.0",
    address: "419 E Hamilton Ave, Campbell, CA 95008",
    phone: "+1 (650) 328-1001",
    email: "info@careindeed.com",
    hours: "9:00 AM - 5:30 PM",
    link: "/campbell",
  },
];

const FindALocation: React.FC<FindALocationProps> = ({
  heading = "Our Service Areas",
  locations = defaultLocations,
  overrideServiceArea = false,
  serviceAreaKMZ = "",
  showServiceArea = false,
}) => {
  const [currentUserCoordinates, setCurrentUserCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const size = useWindowSize();
  const [serviceAreaPolygons, setServiceAreaPolygons] = useState<Polygon[]>([]);
  const [nearestLocation, setNearestLocation] = useState<Location | null>(null);
  const [isInServiceArea, setIsInServiceArea] = useState<boolean>(false);
  const [zoom, setZoom] = useState(10);

  useEffect(() => {
    if (overrideServiceArea && Boolean(serviceAreaKMZ)) {
      parseMapFile(serviceAreaKMZ).then((polygons) => {
        setServiceAreaPolygons(polygons);
      });
    } else {
      parseKMZFile().then((polygons) => {
        setServiceAreaPolygons(polygons);
      });
    }
  }, [overrideServiceArea, serviceAreaKMZ]);

  const handleCoordinatesUpdate = (coordinates: {
    lat: number;
    lng: number;
  }) => {
    setCurrentUserCoordinates(coordinates);

    const isInArea = serviceAreaPolygons.some((polygon) =>
      isPointInPolygon([coordinates.lat, coordinates.lng], polygon)
    );

    setIsInServiceArea(isInArea);

    if (isInArea) {
      const nearest = findNearestLocation(
        coordinates.lat,
        coordinates.lng,
        locations
      );
      setNearestLocation(nearest);
    } else {
      setNearestLocation(null);
    }
  };

  const handleResetNearestLocation = () => {
    setCurrentUserCoordinates(null);
    setNearestLocation(null);
  };

  const handleCloseNoLocationNearYourSearch = () => {
    setIsInServiceArea(false);
    setCurrentUserCoordinates(null);
  };

  return (
    <section className="flex w-full flex-col">
      <div className="flex w-full flex-col gap-4 p-4 md:gap-6 md:px-6 md:py-8 lg:mx-auto lg:max-w-lg lg:gap-8 lg:px-3 lg:py-10">
        <h2 className="w-full text-center font-montserrat text-heading-4 text-secondary-500 md:text-heading-3 lg:text-heading-2">
          {heading}
        </h2>
        <div className="relative flex h-[700px] w-full border lg:h-[542px]">
          {!isInServiceArea && currentUserCoordinates && size !== "lg" && (
            <>
              <div className="absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center bg-black/20" />
              <NoLocationNearYourSearch
                type="modal"
                onClose={handleCloseNoLocationNearYourSearch}
              />
            </>
          )}
          <APIProvider
            apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!}
            onError={(e) => console.log(JSON.stringify(e, null, 2))}
          >
            <CustomMap
              locations={locations}
              zoom={zoom}
              onZoomChanged={setZoom}
              serviceAreaPolygons={serviceAreaPolygons}
              searchedLocation={currentUserCoordinates}
              nearestLocation={nearestLocation}
              showServiceArea={showServiceArea}
            />
          </APIProvider>
          <div className="pointer-events-none absolute bottom-2 left-2 right-2 flex flex-col-reverse gap-2 lg:bottom-4 lg:left-4 lg:top-4 lg:flex-row lg:justify-between">
            <FindCareNearYou
              onHandleCurrentUserCoordinates={handleCoordinatesUpdate}
              isInServiceArea={isInServiceArea}
              onResetNearestLocation={handleResetNearestLocation}
            />
            <ZoomControls
              zoomIn={() => {
                setZoom(zoom + 1);
              }}
              zoomOut={() => {
                setZoom(zoom - 1);
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default FindALocation;
